import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, CheckCheck } from "lucide-react";

export default function ScenariosPage() {
  const { projectId } = useParams();
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [globalShift, setGlobalShift] = useState(5);

  const loadList = useCallback(() => {
    api.get(`/projects/${projectId}/scenarios`).then((r) => setScenarios(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { loadList(); }, [loadList]);

  const openScenario = async (id) => {
    try { const { data } = await api.get(`/projects/${projectId}/scenarios/${id}`); setActive(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const create = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/scenarios`, { name: name || undefined });
      setName(""); loadList(); openScenario(data.id);
      toast.success("Scenario created from the live plan");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/scenarios/${id}`);
      if (active?.id === id) setActive(null);
      loadList();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shiftTask = async (taskId, days) => {
    try { await api.patch(`/projects/${projectId}/scenarios/${active.id}/tasks/${taskId}`, { shiftDays: days }); openScenario(active.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setDuration = async (taskId, durationDays) => {
    try { await api.patch(`/projects/${projectId}/scenarios/${active.id}/tasks/${taskId}`, { durationDays }); openScenario(active.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const shiftAll = async (days) => {
    try { await api.post(`/projects/${projectId}/scenarios/${active.id}/shift`, { days }); openScenario(active.id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const applyToLive = async () => {
    if (!window.confirm(`Apply "${active.name}" to the live plan? This overwrites live task dates and durations with the scenario values.`)) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/scenarios/${active.id}/apply`);
      toast.success(`Scenario applied — ${data.applied} task${data.applied === 1 ? "" : "s"} updated${data.skipped ? `, ${data.skipped} skipped (removed from live plan)` : ""}`);
      openScenario(active.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delta = active?.comparison?.deltaDays;

  return (
    <div data-testid="scenarios-page">
      <PageHeader overline="Plan · What-if analysis" title="Scenario Planning">
        <div className="text-xs text-muted-foreground mt-1">Duplicate the live plan, shift dates or durations, and compare the impact on the end date and critical path — without touching the real schedule.</div>
      </PageHeader>

      <div className="flex gap-4 mt-3">
        <div className="w-60 shrink-0">
          <div className="flex gap-1 mb-2">
            <Input data-testid="scenario-name-input" className="h-9" placeholder="Scenario name…" value={name} onChange={(e) => setName(e.target.value)} />
            <Button data-testid="scenario-create-btn" className="rounded-none h-9 shrink-0" onClick={create}><Plus className="h-4 w-4" /></Button>
          </div>
          {scenarios.map((s) => (
            <div key={s.id} data-testid="scenario-item"
              className={`flex items-center justify-between border px-3 py-2 mb-1 cursor-pointer text-sm ${active?.id === s.id ? "border-[#1E6FE8] bg-[#1E6FE8]/5 font-semibold" : "border-border bg-card"}`}
              onClick={() => openScenario(s.id)}>
              <span className="truncate">{s.name}</span>
              <button data-testid="scenario-delete-btn" onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-muted-foreground hover:text-red-600 ml-2"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {scenarios.length === 0 && <div className="text-xs text-muted-foreground">No scenarios yet — create one to snapshot the current plan.</div>}
        </div>

        <div className="flex-1 min-w-0">
          {!active ? (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm">Select or create a scenario to start exploring.</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-4 gap-3 mb-3" data-testid="scenario-comparison">
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Live plan end</div>
                  <div className="font-display font-black text-xl tracking-tighter">{active.live?.projectEnd || "—"}</div>
                </div>
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Scenario end</div>
                  <div data-testid="scenario-end-date" className="font-display font-black text-xl tracking-tighter text-[#1E6FE8]">{active.projectEnd || "—"}</div>
                </div>
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Impact</div>
                  <div data-testid="scenario-delta" className={`font-display font-black text-xl tracking-tighter ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-600" : ""}`}>
                    {delta == null ? "—" : delta === 0 ? "No change" : `${delta > 0 ? "+" : ""}${delta} days`}
                  </div>
                </div>
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Critical path changes</div>
                  <div className="text-xs mt-1">
                    {(active.comparison?.criticalAdded || []).slice(0, 2).map((n) => <div key={n} className="text-red-600 truncate">+ {n}</div>)}
                    {(active.comparison?.criticalRemoved || []).slice(0, 2).map((n) => <div key={n} className="text-emerald-600 truncate">− {n}</div>)}
                    {!(active.comparison?.criticalAdded || []).length && !(active.comparison?.criticalRemoved || []).length && <span className="text-muted-foreground">Same critical path</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-2 text-sm">
                <span className="text-muted-foreground">Shift entire scenario:</span>
                <Input data-testid="scenario-shift-days-input" type="number" className="w-20 h-8" value={globalShift} onChange={(e) => setGlobalShift(parseInt(e.target.value, 10) || 0)} />
                <Button data-testid="scenario-shift-back-btn" variant="outline" size="sm" className="rounded-none h-8" onClick={() => shiftAll(-Math.abs(globalShift))}><ChevronsLeft className="h-3.5 w-3.5" /> earlier</Button>
                <Button data-testid="scenario-shift-fwd-btn" variant="outline" size="sm" className="rounded-none h-8" onClick={() => shiftAll(Math.abs(globalShift))}>later <ChevronsRight className="h-3.5 w-3.5" /></Button>
                <div className="flex-1" />
                <Button data-testid="scenario-apply-btn" size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={applyToLive}>
                  <CheckCheck className="h-3.5 w-3.5 mr-1" /> Apply to live plan
                </Button>
              </div>

              <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-380px)]">
                <table className="w-full data-table text-sm">
                  <thead><tr><th>WBS</th><th>Task</th><th>Start</th><th>Dur (d)</th><th>Computed finish</th><th>Float</th><th>Critical</th><th className="w-24">Shift</th></tr></thead>
                  <tbody>
                    {(active.tasks || []).map((t) => (
                      <tr key={t.id} data-testid="scenario-task-row" className={t.computedCritical ? "!bg-red-50/50" : ""}>
                        <td className="font-mono2 text-xs whitespace-nowrap">{t.wbsCode}</td>
                        <td className="max-w-xs truncate font-medium">{t.name}</td>
                        <td className="whitespace-nowrap">{t.plannedStart || "—"}</td>
                        <td className="p-1">
                          <input data-testid={`scenario-dur-${t.id}`} type="number" min="0" defaultValue={t.durationDays}
                            onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v !== t.durationDays && v >= 0) setDuration(t.id, v); }}
                            className="w-14 border border-input px-1 py-0.5 text-xs text-center bg-background" />
                        </td>
                        <td className="whitespace-nowrap">{t.computedFinish}</td>
                        <td>{t.totalFloat ?? "—"}</td>
                        <td>{t.computedCritical ? <StatusBadge value="Critical" tone="red" /> : ""}</td>
                        <td className="whitespace-nowrap">
                          <button data-testid={`scenario-shift-minus-${t.id}`} onClick={() => shiftTask(t.id, -1)} className="p-1 hover:text-[#1E6FE8]" title="1 day earlier"><ChevronLeft className="h-3.5 w-3.5" /></button>
                          <button data-testid={`scenario-shift-plus-${t.id}`} onClick={() => shiftTask(t.id, 1)} className="p-1 hover:text-[#1E6FE8]" title="1 day later"><ChevronRight className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
